




import { useEffect, useState } from "react";
import apiSuperAdmin from "./api.js";
import getSocket from "services/socket.js";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import Skeleton from "components/ui/Skeleton.jsx";
import { useAlert } from "context/AlertContext";
import Avatar from "components/ui/Avatar";
import {
    ShieldCheck,
    Shield,
    Briefcase,
    User,
    Circle,
    Zap,
    Clock,
    Sun,
    CalendarRange,
    CalendarDays,
} from "lucide-react";
import StatusFilter from "modules/swiftboard/components/statusFilter.jsx";

const SORT_OPTIONS = [
    { value: "last_active", label: "Recently active" },
    { value: "name", label: "Name (A-Z)" },
    { value: "role", label: "Role" },
];

const SORT_DEFAULT_DIRECTION = { last_active: "desc", name: "asc", role: "asc" };

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

// Activity bucket definitions — drives the stat cards, the API filter, and
// the empty-state copy. "ONLINE" is live status; the rest are windows over
// last_active so users who've since gone offline still surface.
const ACTIVITY_BUCKETS = [
    {
        key: "ONLINE",
        label: "Online Now",
        icon: Zap,
        accent: {
            iconBg: "bg-success_bg text-success",
            ring: "ring-success border-success",
            bar: "var(--color-success)",
        },
        emptyTitle: "No Online Users",
        emptyMessage: "There are no users currently online.",
    },
    {
        key: "LAST_HOUR",
        label: "Last Hour",
        icon: Clock,
        accent: {
            iconBg: "bg-info_bg text-info",
            ring: "ring-info border-info",
            bar: "var(--color-info)",
        },
        emptyTitle: "No Activity in the Last Hour",
        emptyMessage: "No one has been active in the last hour.",
    },
    {
        key: "TODAY",
        label: "Today",
        icon: Sun,
        accent: {
            iconBg: "bg-warning_bg text-warning",
            ring: "ring-warning border-warning",
            bar: "var(--color-warning)",
        },
        emptyTitle: "No Activity Today",
        emptyMessage: "No one has been active today.",
    },
    {
        key: "WEEK",
        label: "This Week",
        icon: CalendarRange,
        accent: {
            iconBg: "bg-primary_tint text-primary",
            ring: "ring-primary border-primary",
            bar: "var(--color-primary)",
        },
        emptyTitle: "No Activity This Week",
        emptyMessage: "No one has been active this week.",
    },
    {
        key: "MONTH",
        label: "This Month",
        icon: CalendarDays,
        accent: {
            iconBg: "bg-secondary_tint text-secondary",
            ring: "ring-secondary border-secondary",
            bar: "var(--color-secondary)",
        },
        emptyTitle: "No Activity This Month",
        emptyMessage: "No one has been active this month.",
    },
];

function ActivityStatCard({ bucket, count, active, loading, onClick }) {
    const Icon = bucket.icon;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`relative flex-1 min-w-[150px] text-left bg-surface rounded-2xl border p-4 pt-[18px] flex items-start gap-3 overflow-hidden cursor-pointer ${active ? `${bucket.accent.ring} ring-1` : "border-border"
                }`}
        >
            <div
                className="absolute inset-x-0 top-0 h-[3px]"
                style={{ background: `linear-gradient(90deg, transparent, ${bucket.accent.bar}, transparent)` }}
            />
            {loading ? (
                <Skeleton className="w-10 h-10 flex-shrink-0" rounded="rounded-xl" />
            ) : (
                <div className={`w-10 h-10 rounded-xl ${bucket.accent.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={18} />
                </div>
            )}
            <div className="min-w-0 flex-1">
                {loading ? (
                    <div className="flex flex-col gap-2 mt-0.5">
                        <Skeleton className="h-[10px] w-20" />
                        <Skeleton className="h-6 w-12" />
                    </div>
                ) : (
                    <>
                        <p className="text-[10.5px] font-semibold text-text_muted uppercase tracking-widest leading-none">
                            {bucket.label}
                        </p>
                        <div className="flex items-baseline gap-2 mt-1.5">
                            <span className="text-[1.75rem] font-bold leading-none tracking-tight text-text_primary">
                                {count ?? 0}
                            </span>
                        </div>
                    </>
                )}
            </div>
        </button>
    );
}

export default function OnlineUsers({ onTotalCountChange }) {
    const { showAlert } = useAlert();

    const [users, setUsers] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [onlineMap, setOnlineMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [resetTrigger, setResetTrigger] = useState(0);
    const [roleFilter, setRoleFilter] = useState("ALL");
    const [exporting, setExporting] = useState(false);
    const [activityFilter, setActivityFilter] = useState("ONLINE");
    const [stats, setStats] = useState({});
    const [statsLoading, setStatsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({
        key: "last_active",
        direction: "desc",
    });

    const activeBucket = ACTIVITY_BUCKETS.find((b) => b.key === activityFilter) || ACTIVITY_BUCKETS[0];

    // Role configuration for badges
    const roleConfig = {
        SUPER_ADMIN: {
            icon: ShieldCheck,
            className: "bg-primary_tint text-primary border-primary",
            label: "Super Admin",
        },
        ADMIN: {
            icon: Shield,
            className: "bg-danger_bg text-danger border-danger_border",
            label: "Admin",
        },
        CLIENT: {
            icon: Briefcase,
            className: "bg-info_bg text-info border-info_border",
            label: "Client",
        },
        EMPLOYEE: {
            icon: User,
            className: "bg-success_bg text-success border-success_border",
            label: "Employee",
        },
    };

    useEffect(() => {
        fetchUsers();
    }, [currentPage, pageSize, searchText, roleFilter, activityFilter]);

    useEffect(() => {
        const socket = getSocket();
        socket.connect();

        socket.on("user-status-updated", ({ userId, isOnline, lastActive }) => {
            setOnlineMap(prev => ({
                ...prev,
                [userId]: { is_online: isOnline, last_active: lastActive }
            }));

            // Refresh the list to show/hide user based on online status
            fetchUsers(false);
        });

        return () => {
            socket.off("user-status-updated");
        };
    }, []);

    const fetchUsers = async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setStatsLoading(true);
        try {
            const payload = {
                skip: (currentPage - 1) * pageSize,
                limit: pageSize,
                searchText: searchText,
                activityFilter,
                ...(roleFilter !== "ALL" && {
                    role: roleFilter
                }),
            };

            const res = await apiSuperAdmin.getAllOnlineUsers(payload);

            if (res.data.status) {
                setUsers(res.data.data);
                setTotalCount(res.data.total_count || 0);
                onTotalCountChange?.(res.data.total_count || 0);
                setStats(res.data.stats || {});

                // Update online map with fetched data
                const initialOnline = {};
                res.data.data.forEach(user => {
                    initialOnline[user.id] = {
                        is_online: user.is_online || false,
                        last_active: user.last_active || null,
                    };
                });
                setOnlineMap(prev => ({ ...prev, ...initialOnline }));
            } else {
                showAlert({ type: 'warning', message: res.data?.message });
            }
        } catch (error) {
            console.error("Error fetching online users:", error);
            if (error.response) {
                showAlert({ type: 'error', message: error.response?.data?.message });
            } else {
                showAlert({ type: 'error', message: "An unexpected error occurred. Please try again!" });
            }
        } finally {
            if (showLoading) setLoading(false);
            setStatsLoading(false);
        }
    };

    const handleActivityFilterChange = (key) => {
        setActivityFilter(key);
        setCurrentPage(1);
    };

    const getLastActiveTime = (lastActive) => {
        if (!lastActive) return "-";
        const date = new Date(lastActive);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setCurrentPage(1);
    };

    const handlePageChange = (page, newPageSize = pageSize) => {
        setCurrentPage(page);
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setCurrentPage(1);
        }
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const handleRefresh = () => {
        setSearchText('');
        setCurrentPage(1);
        setResetTrigger(prev => prev + 1);
        fetchUsers();
    };

    const handleSortChange = (value) => {
        setSortConfig({ key: value, direction: SORT_DEFAULT_DIRECTION[value] || "asc" });
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await apiSuperAdmin.getAllOnlineUsers({
                searchText,
                skip: 0,
                limit: totalCount || 1000,
                activityFilter,
                ...(roleFilter !== "ALL" && { role: roleFilter }),
            });
            const rows = response.data?.data || [];

            if (rows.length === 0) {
                showAlert({ type: "warning", message: "There is no data to export." });
                return;
            }

            const header = ["Name", "Email", "Role", "Designation", "Last Active", "Last Login"];
            const lines = rows.map((row) =>
                [
                    `${row.first_name || ""} ${row.last_name || ""}`.trim(),
                    row.email,
                    row.user_role?.role,
                    row.designation?.designation_name,
                    onlineMap[row.id]?.last_active || row.last_active,
                    row.last_login_date,
                ]
                    .map(csvCell)
                    .join(",")
            );
            const csv = [header.map(csvCell).join(","), ...lines].join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `user-activity-${activityFilter.toLowerCase()}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            showAlert({ type: "error", message: error.response?.data?.message || "Failed to export data" });
        } finally {
            setExporting(false);
        }
    };

    // Sort users based on sortConfig (latest online first)
    const getSortedData = () => {
        if (!sortConfig.key) return users;

        const sorted = [...users].sort((a, b) => {
            let aValue, bValue;

            switch (sortConfig.key) {
                case "name":
                    aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
                    bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
                    break;
                case "role":
                    aValue = a.user_role?.role || '';
                    bValue = b.user_role?.role || '';
                    break;
                case "last_active":
                    aValue = onlineMap[a.id]?.last_active || a.last_active || 0;
                    bValue = onlineMap[b.id]?.last_active || b.last_active || 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });

        return sorted;
    };

    const columns = [
        {
            key: "status",
            label: "Status",
            width: "100px",
            sortable: true,
            render: (row) => {
                const status = onlineMap[row.id];
                const isOnline = status?.is_online || row.is_online;
                return (
                    <div className="flex items-center gap-2">
                        <div className={`relative`}>
                            <Circle
                                size={10}
                                className={`${isOnline ? 'text-success fill-success' : 'text-border fill-border'}`}
                            />
                        </div>
                        <span className={`text-xs font-medium ${isOnline ? 'text-success' : 'text-text_muted'}`}>
                            {isOnline ? 'Online' : 'Offline'}
                        </span>
                    </div>
                );
            },
        },
        {
            key: "name",
            label: "User",
            width: "250px",
            sortable: true,
            render: (row) => (
                <div className="flex items-center gap-3">
                    <div className="relative">
                        {row?.profile_picture_url ? (
                            <img
                                src={row.profile_picture_url}
                                alt={`${row.first_name} ${row.last_name}`}
                                className="w-10 h-10 rounded-full object-cover border-2 border-surface shadow"
                            />
                        ) : (
                            <Avatar user={row} size={36} rounded />
                        )}
                        {onlineMap[row.id]?.is_online && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-surface rounded-full"></span>
                        )}
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-text_primary">
                            {row.first_name} {row.last_name}
                        </span>
                        <span className="text-xs text-text_muted">
                            {row.email}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            key: "role",
            label: "Role",
            width: "160px",
            sortable: true,
            render: (row) => {
                const roleKey = row?.user_role?.role;
                const config = roleConfig[roleKey];

                if (!config) {
                    return (
                        <span className="text-xs text-text_muted">
                            Unknown Role
                        </span>
                    );
                }

                const Icon = config.icon;

                return (
                    <div
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${config.className}`}
                    >
                        {Icon && <Icon size={14} />}
                        {config.label}
                    </div>
                );
            },
        },
        {
            key: "designation",
            label: "Designation",
            width: "180px",
            render: (row) => (
                <span className="text-xs text-text_secondary">
                    {row.designation?.designation_name || '-'}
                </span>
            ),
        },
        {
            key: "last_active",
            label: "Last Active",
            width: "180px",
            sortable: true,
            render: (row) => {
                const status = onlineMap[row.id];
                const lastActive = status?.last_active || row.last_active;
                return (
                    <div className="flex flex-col">
                        <span className="text-xs font-medium text-text_primary">
                            {getLastActiveTime(lastActive)}
                        </span>
                        {lastActive && (
                            <span className="text-xs text-text_muted">
                                {new Date(lastActive).toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            key: "last_login",
            label: "Last Login",
            width: "160px",
            render: (row) => (
                <span className="text-xs text-text_muted">
                    {row.last_login_date ? new Date(row.last_login_date).toLocaleDateString() : '-'}
                </span>
            ),
        },
    ];

    // The API already scopes `users` to the selected activity bucket, so no
    // client-side re-filtering is needed — just sort what came back.
    const sortedData = getSortedData();

    // TableGrid sizes itself internally (fixed viewport-relative height), so
    // this page just lets its natural content height flow and scrolls the
    // whole thing when it overflows — nothing above gets clipped or forced
    // to zero height.
    return (
        <div className="h-full overflow-y-auto bg-surface_alt">
            <div className="flex flex-col gap-4 pb-4">

                {/* Activity stat cards — double as the bucket filter */}
                <div className="flex flex-wrap gap-3">
                    {ACTIVITY_BUCKETS.map((bucket) => (
                        <ActivityStatCard
                            key={bucket.key}
                            bucket={bucket}
                            count={stats[bucket.key]}
                            active={activityFilter === bucket.key}
                            loading={statsLoading}
                            onClick={() => handleActivityFilterChange(bucket.key)}
                        />
                    ))}
                </div>

                <TableToolBar
                    onSearch={handleSearch}
                    onRefresh={handleRefresh}
                    selectedCount={selectedRows.length}
                    resetSearchTrigger={resetTrigger}
                    canCreate={false}
                    canDelete={false}
                    resultCount={sortedData.length}
                    sortOptions={SORT_OPTIONS}
                    sortValue={sortConfig.key}
                    onSortChange={handleSortChange}
                    onExport={handleExport}
                    exporting={exporting}
                />

                {/* Role Filters */}
                <StatusFilter
                    label="Filter by role:"
                    options={[
                        "ALL",
                        "SUPER_ADMIN",
                        "ADMIN",
                        "EMPLOYEE",
                        "CLIENT"
                    ]}
                    selected={roleFilter}
                    onChange={(role) => {
                        setRoleFilter(role);
                        setCurrentPage(1);
                    }}
                />

                {sortedData.length === 0 && !loading ? (
                    <div className="flex items-center justify-center border rounded-lg border-border bg-surface min-h-[320px]">
                        <NoDataState
                            icon="users"
                            title={activeBucket.emptyTitle}
                            message={activeBucket.emptyMessage}
                            showRefresh={false}
                            showBack={false}
                            size="medium"
                        />
                    </div>
                ) : (
                    <>
                        <div className="overflow-hidden border rounded-lg border-border">
                            <TableGrid
                                data={sortedData}
                                totalCount={totalCount}
                                loading={loading}
                                currentPage={currentPage}
                                columns={columns}
                                sortConfig={sortConfig}
                                onSort={handleSort}
                                onSelectionChange={setSelectedRows}
                                pageSize={pageSize}
                                selectable={false}
                                renderActions={() => null}
                            />
                        </div>

                        <Pagination
                            totalCount={totalCount}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
